import { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "react-bootstrap";
import { toast } from "react-toastify";
import EmailEditor from "react-email-editor";
import PropTypes from "prop-types";
import { HtmlPreview } from "Components/Modal";
import { newAuthService } from "Service/Axios";
import {
  GET_OPERATOR_LIST_BY_PROJECT_QUESTIONS_URL,
  GET_QUESTION_LIST_BY_PROJECT_URL,
  GET_VALUES_LIST_BY_PROJECT_QUESTIONS_URL,
} from "Constant/api";
import { Validator } from "Utils/validator";
import { DEFAULT_MODULE_TYPE } from "Constant/constants";
import { ButtonLoader, LoadingComponent, TiptapTextEditor } from "Components";
import { useProjectList } from "Constant/apiHooks";

export const EmailTemplateForm = ({
  initialData,
  variableList,
  variableListForEmailEdtor,
  headerTitle,
  onCancel,
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation();
  const emailEditorRef = useRef(null);
  const textEditorRef = useRef(null);

  const defaultFormData = {
    templateName: "",
    templateCategory: "",
    templateDescription: "",
    projectId: "",
    templateContent: "",
    templateAttachment: [],
    templateWishList: false,
    templateType: "editor",
    templateJson: null,
  };

  const [isPreviewHtmlTemplate, setIsPreviewHtmlTemplate] = useState(false);
  const [questionDataLoading, setQuestionDataLoading] = useState(false);
  const [questionsData, setQuestionData] = useState(null);
  const [questionList, setQuestionList] = useState([]);
  const [formData, setFormData] = useState(defaultFormData);
  const [errMsg, setErrMsg] = useState({
    templateName: "",
    templateCategory: "",
    templateDescription: "",
    projectId: "",
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const {
    templateName,
    templateCategory,
    templateDescription,
    projectId,
    templateContent,
    templateAttachment,
    templateType,
    templateJson,
  } = formData;

  const { projectList, loading: projectListLoading } = useProjectList();

  const modifiedProjectList = useMemo(() => {
    return projectList?.map((project) => ({
      label: project.name,
      value: project.gid,
    }));
  }, [projectList]);

  // Modal handlers
  const togglePreviewHtmlTemplate = () => {
    setIsPreviewHtmlTemplate(!isPreviewHtmlTemplate);
  };

  const getQuestionList = async (projectId) => {
    try {
      setQuestionDataLoading(true);
      const payload = {
        gid: projectId,
      };
      const response = await newAuthService(
        GET_QUESTION_LIST_BY_PROJECT_URL,
        "POST",
        payload
      );
      if (response.status) {
        const questionList = response.data.map((question) => ({
          label: question.name,
          value: question.questionId,
        }));
        setQuestionList(questionList);
      } else {
        setQuestionList([]);
      }
    } catch (e) {
      toast.error(e);
    } finally {
      setQuestionDataLoading(false);
    }
  };

  const getQuestionData = async (questionId, conditionId) => {
    setQuestionDataLoading(true);
    const payload = {
      questionId,
      projectId,
    };
    if (questionId && projectId) {
      const operatorResponse = await newAuthService(
        GET_OPERATOR_LIST_BY_PROJECT_QUESTIONS_URL,
        "POST",
        payload
      );

      const valuesResponse = await newAuthService(
        GET_VALUES_LIST_BY_PROJECT_QUESTIONS_URL,
        "POST",
        payload
      );
      if (operatorResponse.status && valuesResponse.status) {
        const updatedOperators = operatorResponse.data.map((op) => ({
          label: op.name,
          value: op.name,
        }));
        const newObject = {
          [conditionId]: {
            values: valuesResponse.data,
            operators: updatedOperators,
          },
        };
        setQuestionData((prevQuestionData) => ({
          ...prevQuestionData,
          ...newObject,
        }));
      } else {
        const newObject = {
          [conditionId]: {
            values: null,
            operators: [],
          },
        };
        setQuestionData((prevQuestionData) => ({
          ...prevQuestionData,
          ...newObject,
        }));
      }
    }
    setQuestionDataLoading(false);
  };

  // Form handlers
  const handleChange = (e) => {
    if (e.target.type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));

      if (e.target.name === "templateType") {
        setFormData((prev) => ({
          ...prev,
          templateContent: "",
          templateJson: null,
        }));
      }

      if (e.target.name === "templateName") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            templateName: t(
              "common.messages.error.email.template.templateName.blank"
            ),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            templateName: "",
          }));
        }
      }

      if (e.target.name === "templateCategory") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            templateCategory: t(
              "common.messages.error.email.template.templateCategory.blank"
            ),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            templateCategory: "",
          }));
        }
      }

      if (e.target.name === "templateDescription") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            templateDescription: t(
              "common.messages.error.email.template.templateDescription.blank"
            ),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            templateDescription: "",
          }));
        }
      }

      if (e.target.name === "projectId") {
        getQuestionList(e.target.value);
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            projectId: t("common.messages.error.email.rules.projectId.blank"),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            projectId: "",
          }));
        }
      }
    }
  };

  const handleChangeTemplateContent = (value) => {
    setFormData((prev) => ({
      ...prev,
      templateContent: value,
    }));
  };

  const handleAddVariables = (v) => {
    if (textEditorRef.current) {
      const cursorPosition = textEditorRef.current.selectionStart;
      const currentContent = textEditorRef.current.value;
      const newText = `${currentContent.slice(
        0,
        cursorPosition
      )}{${v}}${currentContent.slice(cursorPosition)}`;
      textEditorRef.current.value = newText;
    }
  };

  const handleChangeTemplateAttachment = (newDocument) => {
    setFormData((prev) => ({
      ...prev,
      templateAttachment: [...prev.templateAttachment, newDocument],
    }));
  };

  const handleRemoveTemplateAttachment = (gid) => {
    const updatedDocuments = templateAttachment.filter(
      (doc) => doc.gid !== gid
    );
    setFormData((prev) => ({
      ...prev,
      templateAttachment: updatedDocuments,
    }));
  };

  const handleSaveDesign = () => {
    const unlayer = emailEditorRef.current?.editor;
    unlayer?.exportHtml((data) => {
      const { html } = data;
      setFormData((prev) => ({
        ...prev,
        templateContent: html,
      }));
    });
    unlayer?.saveDesign((design) => {
      setFormData((prev) => ({
        ...prev,
        templateJson: design,
      }));
    });
  };

  const handleLoadDesign = () => {
    if (templateJson) {
      const unlayer = emailEditorRef.current?.editor;
      unlayer?.loadDesign(templateJson);
    }
  };

  // Validation
  const getValidation = (formData) => {
    return new Promise((resolve) => {
      if (
        Validator.text(formData.templateName) &&
        Validator.text(formData.templateCategory) &&
        Validator.text(formData.templateDescription)
      ) {
        resolve(true);
      } else {
        if (!Validator.text(formData.templateName)) {
          setErrMsg((prev) => ({
            ...prev,
            templateName: t(
              "common.messages.error.email.template.templateName.blank"
            ),
          }));
        }

        if (!Validator.text(formData.templateCategory)) {
          setErrMsg((prev) => ({
            ...prev,
            templateCategory: t(
              "common.messages.error.email.template.templateCategory.blank"
            ),
          }));
        }

        if (!Validator.text(formData.templateDescription)) {
          setErrMsg((prev) => ({
            ...prev,
            templateDescription: t(
              "common.messages.error.email.template.templateDescription.blank"
            ),
          }));
        }

        if (!Validator.text(formData.projectId)) {
          setErrMsg((prev) => ({
            ...prev,
            projectId: t("common.messages.error.email.rules.projectId.blank"),
          }));
        }

        resolve(false);
      }
    });
  };

  const handleSubmit = async () => {
    const validationStatus = await getValidation(formData);
    if (validationStatus) {
      onSubmit(formData);
    }
  };

  const handleResetQuestionData = () => {
    setQuestionData(null);
  };

  return (
    <LoadingComponent isLoading={projectListLoading}>
      <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 p-0 email-details">
        <div className="email-details-box">
          <div className="email-header px-5 py-3 bg-light">
            <div className="d-flex align-items-center">
              <button
                className="btn btn-back btn-tranparent d-flex justify-content-center align-items-center border-0 rounded-circle"
                onClick={onCancel}
              >
                <i className="bx bx-arrow-back bx-sm"></i>
              </button>
              <h3 className="h6 ms-1 mb-0">{headerTitle}</h3>
            </div>
          </div>
          <div className="email-body p-4">
            <div className="mb-3">
              <input
                type="text"
                className="email-subject form-control plutoinput-form-control"
                name="templateName"
                value={templateName}
                onChange={handleChange}
                placeholder={t("common.placeHolder.emailTemplateName")}
              />
              {errMsg.templateName && (
                <p className="error">{errMsg.templateName}</p>
              )}
            </div>

            {/* Template Category */}
            <div className="mb-3">
              <input
                type="text"
                className="email-subject form-control plutoinput-form-control"
                name="templateCategory"
                value={templateCategory}
                onChange={handleChange}
                placeholder={t("common.placeHolder.emailTemplateCategory")}
              />
              {errMsg.templateCategory && (
                <p className="error">{errMsg.templateCategory}</p>
              )}
            </div>

            {/* Template Description */}
            <div className="mb-3">
              <textarea
                className="email-subject form-control plutoinput-form-control"
                name="templateDescription"
                value={templateDescription}
                onChange={handleChange}
                placeholder={t("common.placeHolder.emailTemplateDescription")}
                rows={4}
              />
              {errMsg.templateDescription && (
                <p className="error">{errMsg.templateDescription}</p>
              )}
            </div>

            <div className="col-lg-12 col-md-12 col-sm-12">
              <div className="mb-4">
                <select
                  className="form-select form-select-md"
                  name="projectId"
                  value={projectId}
                  onChange={handleChange}
                >
                  <option>{t("common.placeHolder.selectProject")}</option>
                  {modifiedProjectList &&
                    modifiedProjectList.length > 0 &&
                    modifiedProjectList.map((prjct, idx) => (
                      <option key={`${prjct.value}-${idx}`} value={prjct.value}>
                        {prjct.label}
                      </option>
                    ))}
                </select>
                {errMsg.projectId && (
                  <p className="error">{errMsg.projectId}</p>
                )}
              </div>
            </div>

            {/* Template Type Selection */}
            <div className="">
              <div className="form-check form-check-inline mb-3">
                <input
                  type="radio"
                  id="editor"
                  value="editor"
                  checked={templateType === "editor"}
                  name="templateType"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.textEditor")}
                </label>
              </div>
              <div className="form-check form-check-inline mb-3">
                <input
                  type="radio"
                  id="html"
                  value="html"
                  checked={templateType === "html"}
                  name="templateType"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.htmlTemplate")}
                </label>
              </div>
              <div className="form-check form-check-inline mb-3">
                <input
                  type="radio"
                  id="dragAndDrop"
                  value="dragAndDrop"
                  checked={templateType === "dragAndDrop"}
                  name="templateType"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.dragAndDropTemplate")}
                </label>
              </div>

              {/* Rich Text Editor */}
              {templateType === "editor" && (
                <div className="TiptapTextEditor-box">
                  <TiptapTextEditor
                    value={templateContent}
                    handleChange={handleChangeTemplateContent}
                    moduleType={DEFAULT_MODULE_TYPE.emailTemplate}
                    documentList={templateAttachment}
                    handleDocumentChange={handleChangeTemplateAttachment}
                    handleDocumentRemove={handleRemoveTemplateAttachment}
                    showCondBlock={true}
                    isDisabledCondBlock={!projectId}
                    condBlockVarList={questionList}
                    condBlockVarListLoading={questionDataLoading}
                    condBlockData={questionsData}
                    condBlockDataLoading={questionDataLoading}
                    resetCondBlockData={handleResetQuestionData}
                    showVariableDropdown={true}
                    variableList={variableList}
                    fetchOperatorsAndValue={getQuestionData}
                  />
                </div>
              )}

              {/* HTML Editor */}
              {templateType === "html" && (
                <>
                  <div className="mb-3">
                    <textarea
                      ref={textEditorRef}
                      className="email-subject form-control plutoinput-form-control h-400"
                      name="templateContent"
                      value={templateContent}
                      onChange={handleChange}
                      placeholder={t("common.placeHolder.emailHtmlTemplate")}
                      rows={4}
                    ></textarea>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      className="btn btn-secondary"
                      onClick={togglePreviewHtmlTemplate}
                    >
                      Preview
                    </button>

                    {variableList && variableList.length > 0 && (
                      <Dropdown
                        className="user-email-dropdown"
                        autoClose="outside"
                      >
                        <Dropdown.Toggle>
                          <i className="bx bx-list-minus"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {variableList.map((v) => (
                            <Dropdown.Item
                              key={v}
                              onClick={() => handleAddVariables(v)}
                            >
                              {v}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </div>
                </>
              )}

              {/* Drag and Drop Editor */}
              {templateType === "dragAndDrop" && (
                <>
                  <div className="mb-3">
                    <EmailEditor
                      ref={emailEditorRef}
                      options={{
                        mergeTags: variableListForEmailEdtor,
                      }}
                      onReady={handleLoadDesign}
                    />
                  </div>
                  <button
                    className="btn btn-outline-primary"
                    onClick={handleSaveDesign}
                  >
                    Save Design
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="email-footer">
            <div className="row align-items-center">
              <div className="col-auto ms-md-auto">
                <ul className="email-btn-list">
                  <li>
                    <button
                      type="button"
                      className="btn btn-tranparent"
                      onClick={onCancel}
                    >
                      {t("common.button.cancel")}
                    </button>
                  </li>
                  <li>
                    {isLoading ? (
                      <ButtonLoader />
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={templateContent === ""}
                      >
                        <i className="bx bx-save"></i> {t("common.button.save")}
                      </button>
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {isPreviewHtmlTemplate && (
          <HtmlPreview
            show={isPreviewHtmlTemplate}
            toggle={togglePreviewHtmlTemplate}
            htmlData={formData.templateContent || ""}
          />
        )}
      </div>
    </LoadingComponent>
  );
};

EmailTemplateForm.propTypes = {
  initialData: PropTypes.object,
  variableList: PropTypes.array,
  variableListForEmailEdtor: PropTypes.array,
  headerTitle: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
